import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatCalendarDate } from '@/shared/lib/datetime';

import { DayCalendar } from './DayCalendar';
import styles from './DateTimeFields.module.css';

interface Props {
  label: string;
  /** `YYYY-MM-DD` or empty. */
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  describedBy?: string;
  autoFocus?: boolean;
}

/**
 * Date control: a button showing the chosen date that opens a calendar right below it
 * (in the page flow, so dialogs never clip it). Choosing a day closes it; Escape closes only
 * the calendar. Dates can only come from the calendar, which stops at the supported years.
 */
export function DateField({
  label,
  value,
  onChange,
  invalid = false,
  describedBy,
  autoFocus = false,
}: Props) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);

  return (
    <div
      className={styles.field}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          trigger.current?.focus();
        }
      }}
    >
      <span id={`${id}-label`} className={styles.label}>
        {label}
      </span>
      <button
        ref={trigger}
        type="button"
        className={styles.control}
        aria-labelledby={`${id}-label ${id}-value`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        autoFocus={autoFocus}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <span aria-hidden>📅</span>
        <span id={`${id}-value`} className={value ? undefined : styles.placeholder}>
          {value ? formatCalendarDate(value, i18n.language, true) : t('calendar.choose')}
        </span>
      </button>
      {open && (
        <div className={styles.popover} role="dialog" aria-label={label}>
          <DayCalendar
            value={value}
            autoFocus
            onSelect={(next) => {
              onChange(next);
              setOpen(false);
              trigger.current?.focus();
            }}
          />
        </div>
      )}
    </div>
  );
}
